// AboutHelp.js
import React from 'react';
import VisitOMSLogo from './visitoms.png';

function AboutHelp() {
  // Ako baš želiš da razlikuješ prikaz po roli:
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'teacher';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* HERO / LOGO */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-sky-600 via-cyan-500 to-emerald-500 text-white shadow-xl mb-4">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff55,_transparent_60%)]" />
        <div className="relative flex flex-col md:flex-row items-center md:items-start gap-6 px-6 py-6 md:px-10 md:py-8">
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <img
              src={VisitOMSLogo}
              alt="VisitOMS logo"
              className="w-24 h-24 md:w-28 md:h-28 object-contain mb-3 bg-white/10 rounded-2xl p-2 shadow-md"
            />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              VisitOMS EarlyCare
            </h1>
            <p className="text-sm md:text-base text-sky-100 mt-1">
              SaaS platform for EarlyCare visits, telehealth &amp; progress tracking
            </p>

            <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur">
                v2.76 • Multi-tenant
              </span>
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur">
                EI • PI • ABA • OT • PT• ST
              </span>
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur">
                Home-based &amp; Telehealth
              </span>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center md:items-end justify-center gap-2 text-xs md:text-sm">
            <div className="flex flex-wrap justify-center md:justify-end gap-2">
              <span className="inline-flex items-center gap-1 rounded-lg bg-black/10 px-3 py-1 font-medium">
                <span className="text-emerald-200 text-[10px] uppercase tracking-widest">
                  CURRENT ROLE
                </span>
                <span className="inline-flex items-center gap-1 text-white">
                  ●{' '}
                  {role === 'admin'
                    ? 'Organization Admin'
                    : role === 'parent'
                    ? 'Parent / Caregiver'
                    : role === 'super_admin'
                    ? 'Super Admin'
                    : 'Teacher / Provider'}
                </span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-lg bg-black/10 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
                <span>Live SaaS environment</span>
              </span>
            </div>
            <p className="mt-2 text-sky-100/80 max-w-xs text-center md:text-right">
              Each organization has its own branding, timezone, settings and security
              boundaries – EarlyCare scales with your agency.
            </p>
          </div>
        </div>
      </div>

      {/* COMPANY INFO */}
      <section className="bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow rounded-2xl p-5 md:p-7 border border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-xl bg-sky-100 flex items-center justify-center text-sky-700">
            <span className="text-lg">ⓘ</span>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-sky-600 uppercase">
              Platform overview
            </p>
            <h2 className="text-xl md:text-2xl font-semibold">About VisitOMS</h2>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-2">
          VisitOMS is a modern software solution focused on managing home-based and telehealth
          visits for children aged 0–36 months within Early Intervention (EI) and Project Impact (PI)
	  programs, and now also supports ABA, Speech Therapy (ST), OT, and PT disciplines. The platform is designed to
          support agencies, administrators, and providers who work directly with families in the field
          or online.
        </p>
        <p className="text-sm text-gray-700 mb-2">
          The system combines visit scheduling, smart teacher assignment, geolocation and routing,
          time logging, skill tracking, secure messaging, telehealth, payroll, and reporting into one
          integrated workflow. It is built with real-world constraints in mind: traffic, availability,
          cost optimization, family preferences, and compliance with program rules.
        </p>
        <p className="text-sm text-gray-700 mb-2">
          VisitOMS EarlyCare runs as a multi-tenant SaaS platform. Each organization has its own
          timezone, settings, branding, disciplines, and security boundaries, while super admins can
          support multiple agencies from a single environment.
        </p>
        <p className="text-sm text-gray-700 mb-4">
          The system connects{' '}
          <span className="font-medium">
            super admins, organization admins, teachers/providers, and parents
          </span>{' '}
          in one secure workflow: parents sign in via magic links (no passwords), providers see
          optimized daily plans, admins control routing and workload, and everyone can collaborate
          through secure in-app messaging and telehealth links.
        </p>

        <div className="mt-4 pt-4 border-t border-slate-100 grid md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold mb-1 text-slate-800">
              Company details
            </h3>
            <div className="space-y-1 text-sm text-gray-700">
              <p>
                <span className="font-medium">Company name:</span>{' '}
                VisitOMS
              </p>
              <p>
                <span className="font-medium">Project name:</span> VisitOMS EarlyCare
              </p>
              <p>
                <span className="font-medium">Address:</span>{' '}
                <span className="italic text-gray-500">
                  2801 B St. Ste 11, San Diego, CA 92102, USA
                </span>
              </p>
              <p>
                <span className="font-medium">Contact email:</span>{' '}
                <span className="italic text-gray-500">
                  Info@VisitOMS.com
                </span>
              </p>
            </div>
          </div>

          <div className="md:border-l md:border-slate-100 md:pl-4 space-y-1 text-sm text-gray-700">
            <h3 className="text-sm font-semibold mb-1 text-slate-800">
              Key characteristics
            </h3>
            <p>• Multi-tenant, role-based access model</p>
            <p>• U.S. EI/PI-oriented workflows (California-first)</p>
            <p>• Designed for mobile-first teacher and parent usage</p>
            <p>• Rich audit logs, time logs and visit history</p>
          </div>
        </div>
      </section>

      {/* PROJECT INFO / FEATURES */}
      <section className="bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow rounded-2xl p-5 md:p-7 border border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
            <span className="text-lg">⚙️</span>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-emerald-600 uppercase">
              Product
            </p>
            <h2 className="text-xl md:text-2xl font-semibold">
              About the EarlyCare project
            </h2>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-3">
          EarlyCare was created as a practical tool to support agencies working with infants and
          toddlers in the 0–36 months range. The goal is to replace fragmented spreadsheets,
          manual planning, paper-based time logs, and ad-hoc messaging with one coherent system
          that can grow with the organization.
        </p>

        {/* Feature grid */}
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/60">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-1">
              <span className="text-sm">📅</span>
              Scheduling &amp; disciplines
            </h3>
            <p className="text-xs text-slate-600">
              Daily and weekly visit planning across five disciplines:{' '}
              <span className="font-medium">EI, PI, ABA, OT, PT, ST</span>, each with discipline-aware
              durations and rules, including standard and multi-hour (event) visits.
            </p>
          </div>

          <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/60">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-1">
              <span className="text-sm">🧭</span>
              Smart routing &amp; substitutes
            </h3>
            <p className="text-xs text-slate-600">
              Automatic suggestions for substitute providers when the default teacher is
              unavailable, considering availability, distance (routing APIs) and cost.
            </p>
          </div>

          <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/60">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-1">
              <span className="text-sm">🕒</span>
              Time logs &amp; payroll
            </h3>
            <p className="text-xs text-slate-600">
              Detailed time logs and payroll-ready data for hours worked, drive time, miles, event
              durations and discipline mix – ready for billing and internal analytics.
            </p>
          </div>

          <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/60">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-1">
              <span className="text-sm">📈</span>
              Child progress &amp; reports
            </h3>
            <p className="text-xs text-slate-600">
              Tracking child skills with baselines, domains and achievements, plus student progress
              reports for government requirements and clear, parent-friendly summaries.
            </p>
          </div>

          <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/60">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-1">
              <span className="text-sm">💬</span>
              Messaging &amp; collaboration
            </h3>
            <p className="text-xs text-slate-600">
              Built-in messaging between parents, teachers, organization admins and super admins
              to coordinate visits, cancellations, questions and support in one secure channel.
            </p>
          </div>

          <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/60">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-1">
              <span className="text-sm">🖥️</span>
              Telehealth &amp; parent portal
            </h3>
            <p className="text-xs text-slate-600">
              Secure telehealth visits for any discipline, with separate join links for providers
              and parents, integrated in planners and timelines, plus a magic-link parent portal
              focused on visits, links and progress.
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-700 mt-5">
          Depending on your role, you will see different parts of the system:
        </p>
        <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
          <li>
            <span className="font-medium">Administrators</span> focus on agency-wide settings,
            teacher management, discipline configuration, scheduling, reporting, and payroll.
          </li>
          <li>
            <span className="font-medium">Teachers / Providers</span> focus on their own visits,
            day planning, time logs, child skill tracking, messaging with families, and telehealth.
          </li>
          <li>
            <span className="font-medium">Parents / Caregivers</span> use a simple portal (magic
            link) to see upcoming visits, telehealth join links, messages, and child progress
            summaries.
          </li>
          <li>
            <span className="font-medium">Super admins</span> oversee multiple organizations,
            manage global templates and disciplines, and support agencies via secure messaging and
            joint troubleshooting/training.
          </li>
        </ul>
      </section>

      {/* ROLE-SPECIFIC HELP (kratko) */}
      <section className="bg-slate-950 text-slate-50 rounded-2xl shadow-md p-5 md:p-7 border border-slate-800">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-sky-400 uppercase">
              In-app navigation
            </p>
            <h2 className="text-xl md:text-2xl font-semibold">Quick help</h2>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-300">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span>Tip: you can always return here from the main menu</span>
          </div>
        </div>

        {role === 'admin' ? (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <ul className="space-y-1.5">
              <li>
                • Start in <span className="font-medium text-white">Dashboard</span> for a quick
                overview of visits, teachers and key indicators.
              </li>
              <li>
                • Use <span className="font-medium text-white">Manage Teachers</span> and{' '}
                <span className="font-medium text-white">Manage Students</span> to keep your data
                clean and up to date.
              </li>
              <li>
                • Configure disciplines and system behavior in{' '}
                <span className="font-medium text-white">Admin / System Settings</span> (timezones,
                branding, discipline templates, etc.).
              </li>
            </ul>
            <ul className="space-y-1.5">
              <li>
                • Plan visits in{' '}
                <span className="font-medium text-white">Manage Visits</span> and{' '}
                <span className="font-medium text-white">Smart Visits Scheduler</span>, including
                substitute teacher logic.
              </li>
              <li>
                • Monitor work and payroll inputs in{' '}
                <span className="font-medium text-white">Teachers Work Report</span> and{' '}
                <span className="font-medium text-white">Teacher Time Logs</span>.
              </li>
              <li>
                • Use <span className="font-medium text-white">Messages</span> to coordinate with
                teachers, parents and super-admin support.
              </li>
            </ul>
          </div>
        ) : role === 'parent' ? (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <ul className="space-y-1.5">
              <li>
                • Use your <span className="font-medium text-white">magic-link email</span> to open
                the parent portal – no password required, each link is secured and time-limited.
              </li>
              <li>
                • Check the{' '}
                <span className="font-medium text-white">Today / Upcoming visits</span> view
                (timeline) to see when your child&apos;s teacher is scheduled to visit or start a
                telehealth session.
              </li>
              <li>
                • For online sessions, click the{' '}
                <span className="font-medium text-white">Telehealth join link</span> shown next to
                each telehealth visit.
              </li>
            </ul>
            <ul className="space-y-1.5">
              <li>
                • Open <span className="font-medium text-white">Messages</span> to send secure
                questions or updates to your child&apos;s teacher or to the agency.
              </li>
              <li>
                • Review your child&apos;s development in the{' '}
                <span className="font-medium text-white">Progress / Reports</span> section (when
                enabled by your agency) to see achievements and goals over time.
              </li>
            </ul>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <ul className="space-y-1.5">
              <li>
                • Each day, start with{' '}
                <span className="font-medium text-white">My Day Planner</span> and{' '}
                <span className="font-medium text-white">My Visits</span> to see all scheduled home
                and telehealth sessions.
              </li>
              <li>
                • Use <span className="font-medium text-white">Check-in</span> and{' '}
                <span className="font-medium text-white">Check-out</span> with geolocation when you
                visit a family so that time logs and mileage are captured correctly for payroll.
              </li>
              <li>
                • For telehealth visits, open the{' '}
                <span className="font-medium text-white">Telehealth join link</span> directly from
                your planner or visit list.
              </li>
            </ul>
            <ul className="space-y-1.5">
              <li>
                • Update child skills in{' '}
                <span className="font-medium text-white">My Students Skills</span> and{' '}
                <span className="font-medium text-white">
                  Student Goals &amp; Achievement
                </span>{' '}
                to keep baselines and progress current.
              </li>
              <li>
                • Review your hours, travel and discipline mix in{' '}
                <span className="font-medium text-white">My Time Log</span> and related reports to
                verify payroll data.
              </li>
              <li>
                • Use <span className="font-medium text-white">Messages</span> to coordinate with
                parents, your organization admin and other providers.
              </li>
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

export default AboutHelp;


